import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  AlertCircle, AlertTriangle, Info, ArrowRight, Mail,
} from 'lucide-react';

// Public-facing audit page rendered at /audit/<token>.
// Data shape comes from sudco_agent/audit/schema.py — kept in sync there.
// noindex + nofollow because these URLs are 1:1 to a prospect.
function AuditPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'loading', audit: null, error: null });

  useEffect(() => {
    window.scrollTo(0, 0);
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', audit: null, error: null });

    fetch(`/api/audit/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 410) {
          setState({ status: 'expired', audit: null, error: body.error || 'This audit has expired.' });
        } else if (res.status === 404) {
          setState({ status: 'not_found', audit: null, error: 'Audit not found.' });
        } else if (!res.ok || !body.ok) {
          setState({ status: 'error', audit: null, error: body.error || `Request failed (${res.status})` });
        } else {
          setState({ status: 'ok', audit: body.audit, error: null });
        }
      })
      .catch((err) => {
        if (!cancelled) setState({ status: 'error', audit: null, error: err.message });
      });

    return () => { cancelled = true; };
  }, [token]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-accent border-t-transparent animate-spin" />
          <p className="text-text-muted text-sm">Loading your audit…</p>
        </div>
      </div>
    );
  }

  if (state.status !== 'ok') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="font-display font-bold text-3xl text-text mb-3">
            {state.status === 'expired' ? 'Audit expired' : 'Audit not found'}
          </h1>
          <p className="text-text-muted mb-8">
            {state.error || "We couldn't find that audit. The link may be incorrect or no longer active."}
          </p>
          <Link to="/" className="inline-flex items-center gap-2 bg-gradient-brand text-white px-6 py-3 rounded-full font-semibold hover:shadow-glow transition-shadow">
            Visit Sudco Solutions
          </Link>
        </div>
      </div>
    );
  }

  return <AuditContent audit={state.audit} />;
}

function AuditContent({ audit }) {
  const grouped = useMemo(() => groupBySeverity(audit.findings || []), [audit.findings]);
  const counts = useMemo(() => ({
    high: grouped.high.length,
    medium: grouped.medium.length,
    low: grouped.low.length,
  }), [grouped]);

  return (
    <div className="min-h-screen bg-bg text-text">
      {/* Brand bar — mirrors the email's gradient strip so the page reads as the same sender */}
      <header className="bg-gradient-brand">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center font-display font-bold text-accent text-xl">S</div>
          <div>
            <div className="font-display font-semibold text-white text-base leading-tight">Sudco Solutions</div>
            <div className="text-white/80 text-xs">Small-business websites, reputation &amp; visibility · Houston, TX</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        {/* Title */}
        <p className="text-text-muted text-sm uppercase tracking-wider mb-3">Local Visibility Audit</p>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-text leading-tight mb-3">
          {audit.business_name}
        </h1>
        <p className="text-text-muted text-base mb-8">
          {[audit.location, audit.industry].filter(Boolean).join(' · ')}
          {audit.site_url && (
            <>
              {' · '}
              <a href={audit.site_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                {prettyUrl(audit.site_url)}
              </a>
            </>
          )}
        </p>

        {/* Summary card */}
        <div className="rounded-2xl bg-surface border border-border p-6 md:p-8 mb-10">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-brand flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <p className="font-display text-xl md:text-2xl text-text font-semibold leading-snug">
                {audit.summary || `We found ${audit.findings.length} thing${audit.findings.length === 1 ? '' : 's'} worth fixing on your online presence.`}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {counts.high > 0 && <Pill kind="high">{counts.high} high priority</Pill>}
                {counts.medium > 0 && <Pill kind="medium">{counts.medium} medium</Pill>}
                {counts.low > 0 && <Pill kind="low">{counts.low} small</Pill>}
              </div>
            </div>
          </div>
        </div>

        {/* Findings */}
        <div className="space-y-10">
          {grouped.high.length > 0 && (
            <FindingSection title="High priority" subtitle="The biggest gaps — fix these first." findings={grouped.high} />
          )}
          {grouped.medium.length > 0 && (
            <FindingSection title="Medium priority" subtitle="Real wins, smaller impact." findings={grouped.medium} />
          )}
          {grouped.low.length > 0 && (
            <FindingSection title="Polish" subtitle="Quick fixes worth doing." findings={grouped.low} />
          )}
        </div>

        {/* CTA card */}
        <div className="mt-12 rounded-2xl bg-gradient-brand-soft border border-accent/30 p-7 md:p-9">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-text mb-3">
            Want this all fixed?
          </h2>
          <p className="text-text-muted text-base mb-6">
            I'll fix every finding above
            {audit.fix_package_price ? <> for a flat <span className="text-text font-semibold">{audit.fix_package_price}</span>.</> : '.'}
            {' '}No subscription, no retainer. One-time, done in about a week. If you want any of these
            handled individually instead, that works too.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`mailto:nate@sudcosolutions.com?subject=${encodeURIComponent(`Re: audit for ${audit.business_name}`)}`}
              className="inline-flex items-center justify-center gap-2 bg-gradient-brand text-white px-6 py-3 rounded-full font-semibold hover:shadow-glow transition-shadow"
            >
              <Mail className="w-4 h-4" />
              Reply to nate@sudcosolutions.com
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://sudcosolutions.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-border text-text-muted hover:text-text hover:border-accent px-6 py-3 rounded-full font-semibold transition-colors"
            >
              See more of my work
            </a>
          </div>
        </div>

        {/* Footnote */}
        <p className="text-text-muted text-xs mt-10 text-center">
          Generated {formatDate(audit.generated_at)} by Sudco Solutions ·{' '}
          <a href="mailto:nate@sudcosolutions.com" className="hover:text-accent">nate@sudcosolutions.com</a>
        </p>
      </main>
    </div>
  );
}

function FindingSection({ title, subtitle, findings }) {
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-5">
        <h2 className="font-display font-bold text-xl text-text">{title}</h2>
        <p className="text-text-muted text-sm">{subtitle}</p>
      </div>
      <div className="space-y-4">
        {findings.map((f) => <FindingCard key={f.key} finding={f} />)}
      </div>
    </section>
  );
}

function FindingCard({ finding }) {
  const Icon = severityIcon(finding.severity);
  const iconColor = {
    high: 'text-red-400',
    medium: 'text-amber-400',
    low: 'text-cyan-400',
  }[finding.severity] || 'text-text-muted';

  return (
    <article className="rounded-xl bg-surface border border-border p-5 md:p-6">
      <div className="flex gap-4 items-start">
        <div className={`flex-shrink-0 mt-1 ${iconColor}`}>
          <Icon className="w-5 h-5" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-text text-lg leading-snug mb-2">
            {finding.title}
          </h3>
          <p className="text-text-muted text-sm md:text-base leading-relaxed mb-3">
            {finding.detail}
          </p>
          <div className="text-sm border-l-2 border-accent/50 pl-3 py-1">
            <span className="text-text-muted">How I'd fix it: </span>
            <span className="text-text">{finding.fix_blurb}</span>
            {finding.fix_price && (
              <span className="text-text-muted"> · <span className="text-text font-semibold">{finding.fix_price}</span></span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function Pill({ kind, children }) {
  const styles = {
    high: 'bg-red-500/10 text-red-300 border-red-500/30',
    medium: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    low: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
  }[kind];
  return (
    <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border ${styles}`}>
      {children}
    </span>
  );
}

function groupBySeverity(findings) {
  const out = { high: [], medium: [], low: [] };
  for (const f of findings) {
    if (out[f.severity]) out[f.severity].push(f);
    else out.medium.push(f);
  }
  return out;
}

function severityIcon(sev) {
  if (sev === 'high') return AlertCircle;
  if (sev === 'medium') return AlertTriangle;
  return Info;
}

function prettyUrl(url) {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

export default AuditPage;
